import { getCarImage } from '../save/db.js';
import { inlineSvg, packUrl } from '../content/svg.js';
import { menuIconElement } from '../content/menu-icons.js';
import { pupIdForVehicle, VEHICLES } from '../content/vehicles.js';
import { cropToArtwork } from '../draw/crop.js';
import { applyDecorations } from '../draw/decorate.js';
import { anchorOf, viewBoxOf, windowBoxOf } from '../draw/template.js';
import { brandMark } from './brand.js';
import { packBrandMark } from './brand-alphabet.js';
import { el, GLYPH, GLYPH_ICON, pictureButton, timeout } from './dom.js';
import type { SavedCar, SaveData } from '../core/save/model.js';
import type { Screen, ScreenContext } from './router.js';

/**
 * The Lookout: where the game starts, and where she chooses what to do.
 *
 * This was the last text screen in the game ("The Lookout", "New car", "Drive last", "Garage"), which was
 * fine for walking the loop by hand and useless to the player, who cannot read any of it.
 *
 * It is also the first screen now. There used to be a boot screen in front of it - a green disc with a
 * play triangle - which existed less as a screen than as somewhere to collect the user gesture iOS needs
 * before it will make a sound, and which said nothing about the game. So the intro is this screen's own
 * entrance instead: the name arcs in, the Lookout arrives, and the five slots pop in around it - her pup
 * among them, in the cab of her car. Nothing to press through and no start button.
 *
 * The decisions that matter here:
 *
 *  - **It is one picture, not a background with controls on it.** The name arcs over the top, the sun's
 *    rays fan out from behind the tower, the hills run out to both edges of the screen, and the road
 *    leaves the Lookout's door and comes down between the buttons - so the five slots stand *in the place*
 *    she is about to drive out of. Every layer is CSS or drawn here: nothing in the scene is fetched except
 *    the two pieces of pack art that were already here, and any of it can fail without costing a control.
 *  - **The name is a mark, not a label.** See `brand.ts`. The wordless rule is about words she would have
 *    to *read*; the game's own name is not one of those, and a title is most of what makes this read as a
 *    finished game rather than a menu. It is still held to being artwork: `aria-hidden`,
 *    `pointer-events: none`, and out of the layout the slots live in.
 *  - **Her newest car is the big button, with her pup at the wheel.** Not a car glyph - the actual painted
 *    car, from its baked thumbnail, and the dog who drives it sitting in its cab. "Drive the one I made" is
 *    the thing she wants most often, and a picture of *her* car with *her* pup in it is the only label that
 *    needs no explaining. The pup used to stand on the grass beside the tower instead, which is a nice
 *    picture and says nothing: in the car, she is what the big button is *for*.
 *  - **No car means no pup.** On a fresh save there is nothing of hers on the screen at all - the big slot
 *    is a dashed invitation to draw one, and there is no dog. The generic pup used to come out to meet her
 *    on that first launch, which introduced a stranger as though she were somebody, and gave the emptiest
 *    state on the screen a decoration it had not earned. Everything of hers appears the moment she has made
 *    something, and not before.
 *  - **Positions are fixed, and this time they really are.** Five slots, laid out by grid area, and the
 *    save changes only what is *in* them. The first version claimed this and did the opposite: with an
 *    empty garage the crayon was the big button in the middle, so the moment she saved her first car it
 *    shrank, moved left, and two more buttons appeared beside it - the whole screen rearranging itself
 *    at exactly the moment she was learning where things are.
 *  - **The big slot is always "the car I am going to drive".** Empty, it is a dashed slot with a crayon
 *    in it that starts a new car, so on day one the big slot and the crayon slot do the same thing and
 *    she cannot miss; on day two it fills with a picture of her car and nothing has moved.
 *  - **An impossible action keeps its space but is not there.** The garage and free play slots are held
 *    open and empty until there is a car, rather than greyed out: a button that responds to a press by
 *    doing nothing teaches her that the game is broken.
 *  - **No words and no counts.** The prize button is a treat, not a number of treats.
 *  - **The intro plays once per launch**, not on every return from a mission - which happens after every
 *    single drive, and a 1.3-second wake-up sequence she has to sit through fifteen times an evening is a
 *    delay rather than a welcome. Later mounts still get the slots popping in, which costs her nothing.
 *  - **Nothing is ever unpressable.** The whole screen is built on mount and the intro is pure CSS on
 *    opacity and transform, so a press at the first frame works. She will press instantly, and
 *    "everything responds within 100ms" outranks the animation.
 */

/**
 * Whether the wake-up sequence has already played this launch. Module state rather than save state: it is
 * about this run of the app, and it must not survive a reload - the intro is what greets her when she
 * opens the game.
 */
let introShown = false;

/**
 * How long the parent's corner has to be held, in milliseconds.
 *
 * Three seconds, as specified in `docs/MENUS.md`. Long enough that no press of hers reaches it, and the ring
 * that fades in after the first second (`.hub-gate.is-holding`, in the stylesheet) is what stops a parent
 * holding an apparently dead corner for the other two.
 */
const GATE_HOLD_MS = 3000;

export interface HubScreenOptions {
  /** Injected for tests, and so a missing thumbnail costs the picture rather than the button. */
  readonly loadImage?: (carId: string) => Promise<Blob | undefined>;
  /** Injected for tests: forces the wake-up sequence on or off rather than letting the launch decide. */
  readonly playIntro?: boolean;
  /** Injected for tests, which have no network for the scene's art. */
  readonly loadArt?: (family: string, name: string) => Promise<SVGSVGElement | null>;
}

export function createHubScreen(options: HubScreenOptions = {}): Screen {
  const loadImage = options.loadImage ?? getCarImage;
  const loadArt = options.loadArt ?? ((family, name) => inlineSvg(packUrl(family, name)));

  let disposed = false;
  const objectUrls: string[] = [];
  /** The hold in progress, so leaving the screen mid-hold cannot open the gate behind her. */
  let cancelHold: (() => void) | null = null;

  return {
    mount(root, ctx) {
      const car = mostRecentCar(ctx.currentSave());
      const intro = options.playIntro ?? !introShown;
      introShown = true;

      const screen = el('div', {
        className: `screen screen--hub${intro ? ' is-intro' : ''}`,
        children: [
          discs(),
          clouds(),
          title(() => !disposed),
          scene(loadArt),
          gate(() => ctx.dispatch({ type: 'OPEN_PARENT' })),
          el('div', {
            className: 'hub-main',
            children: [
              hubButton('crayon', 'hub-draw', SLOT.draw, () => newCar(ctx)),
              car === null ? inviteButton(ctx) : driveButton(car, ctx),
              car === null
                ? emptySlot(SLOT.garage)
                : hubButton('garage', 'hub-garage', SLOT.garage, () =>
                    dispatch(ctx, { type: 'OPEN_GARAGE' }),
                  ),
            ],
          }),
          el('div', {
            className: 'hub-side',
            children: [
              hubButton('treat', 'hub-prizes', SLOT.prizes, () =>
                dispatch(ctx, { type: 'OPEN_COLLECTION' }),
              ),
              car === null
                ? emptySlot(SLOT.freeplay)
                : hubButton('outdoors', 'hub-freeplay', SLOT.freeplay, () =>
                    /*
                     * A brand new world every time she presses it. Free play has no return leg to match, so
                     * there is nothing to keep stable - and "somewhere I have not been" is most of the point
                     * of a sandbox.
                     */
                    dispatch(ctx, { type: 'FREE_PLAY', carId: car.id, routeSeed: freshSeed() }),
                  ),
            ],
          }),
        ],
      });

      root.append(screen);
    },

    unmount() {
      disposed = true;
      cancelHold?.();
      cancelHold = null;
      // Blob URLs are never garbage collected on their own; one per visit would add up over a session.
      for (const url of objectUrls) URL.revokeObjectURL(url);
      objectUrls.length = 0;
    },
  };

  /**
   * The parent's door: a three-second hold in the top-right corner of the sky.
   *
   * The corner rather than a button because a button is a thing she presses - and this one has to be
   * reachable by whoever is looking after her without ever being findable by her. Three points hold that up:
   *
   *  - **A hold is not a shape she produces on purpose.** She taps, and she taps hard; three seconds of
   *    stillness on an empty patch of sky is not in her vocabulary. She will manage one by accident, which
   *    is what the sequence on the other side is for.
   *  - **The top right, not the top left.** The paw lives in the top-left corner of every other screen in
   *    the game, so that corner is the one place on this screen she has already been taught to press. The
   *    Lookout has no paw and the badge slots start below the scene, which leaves this corner genuinely
   *    unused - the condition `docs/MENUS.md` asks for.
   *  - **Silent, and invisible until it is nearly earned.** No press sound and nothing drawn for the first
   *    second, so an accidental hold teaches her nothing; over the remaining two a ring closes to say the
   *    door is opening, because a parent needs to know the hold is working and three seconds with no
   *    feedback at all reads as a dead corner.
   *
   * A `div`, not a `button`: it must not be tabbable, must not answer a tap, and must not appear to anyone
   * sweeping the screen for controls.
   */
  function gate(onOpen: () => void): HTMLElement {
    const pad = el('div', { className: 'hub-gate', attrs: { 'aria-hidden': 'true' } });
    // Parent-facing, and the only place in the running game that says how to get in.
    pad.title = 'hold for three seconds for the grown-up settings';

    const stop = (): void => {
      cancelHold?.();
      cancelHold = null;
      pad.classList.remove('is-holding');
    };

    pad.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      // A second finger while one is already down is one hold, not two timers racing.
      if (cancelHold !== null) return;
      pad.classList.add('is-holding');
      cancelHold = timeout(GATE_HOLD_MS, () => {
        stop();
        onOpen();
      });
    });
    for (const type of ['pointerup', 'pointercancel', 'pointerleave'] as const) {
      pad.addEventListener(type, stop);
    }

    return pad;
  }

  /**
   * The big slot when there is nothing to drive yet: a dashed outline with a crayon in it.
   *
   * It does the same thing as the crayon slot beside it, deliberately. The alternative - moving the
   * crayon into the big slot - is what used to rearrange the screen the first time she saved a car.
   */
  function inviteButton(ctx: ScreenContext): HTMLElement {
    return hubButton('crayon', 'hub-invite is-big', SLOT.drive, () => newCar(ctx));
  }

  /**
   * The big one: her car with her pup at the wheel, which drives it.
   *
   * Two pictures of the same car race here, and the better one wins:
   *
   *  - **the baked thumbnail**, which is one read from IndexedDB and arrives almost at once;
   *  - **her car re-drawn from the template**, which needs the pack art over HTTP but is the only one the
   *    pup can be placed on.
   *
   * The thumbnail cannot carry the pup, and the reason is worth writing down because it looked like it
   * could: the studio crops the drawing to its artwork before baking (`cropToArtwork`), so the PNG is some
   * unrecorded window onto template space - a fire truck's window box lands on its door. There is no
   * arithmetic that recovers it. The drive scene never had the problem because it never uses the thumbnail:
   * it decorates the template and composes body, pup, glass. The badge does that now too, which is why the
   * pup sits in the same place here as she does on the road.
   *
   * So the thumbnail is the first rung of a ladder rather than the picture: car glyph, then her thumbnail,
   * then her car with the pup in it. Each rung is strictly better and none of them can fail to a dead
   * button.
   */
  function driveButton(car: SavedCar, ctx: ScreenContext): HTMLElement {
    const button = hubButton('car', 'hub-drive is-big', SLOT.drive, () =>
      dispatch(ctx, { type: 'PICK_CAR', carId: car.id }),
    );
    // The vector arrives second on a cold cache and first on a warm one; whichever it is, it stays.
    let redrawn = false;

    void loadImage(car.id)
      .then((blob) => {
        if (disposed || redrawn || blob === undefined) return;
        const url = URL.createObjectURL(blob);
        objectUrls.push(url);
        const image = el('img', { className: 'hub-car-image', attrs: { alt: '' } });
        image.src = url;
        // Replaces the glyph rather than joining it: her car says "drive" better than a car icon does.
        button.replaceChildren(image);
      })
      .catch(() => {
        // No thumbnail is a car glyph, not a dead button.
      });

    void paintedCar(car)
      .then((art) => {
        if (disposed || art === null) return;
        redrawn = true;
        button.replaceChildren(art);
        /*
         * Cropped only once it is in the document, because `getBBox` has nothing to report before that -
         * and cropped at all because the studio crops the drawing it bakes her thumbnail from. Same
         * function, so the two pictures of her car are the same size and the swap moves nothing.
         */
        cropToArtwork(art);
      })
      .catch(() => {
        // Missing pack art leaves her thumbnail, which is the picture this screen shipped with.
      });

    return button;
  }

  /**
   * Her car, redrawn from the template with everything she did to it - and her pup in the cab.
   *
   * `applyDecorations` is the studio's and the drive scene's own function: colours onto the regions, her
   * crayon marks into the panel they were drawn in, her stickers on the clip. Sharing it is what stops this
   * badge from being a third opinion about what her car looks like. Wheel targets are not passed, which is
   * the one difference from the road: a sticker stuck on a tyre lands in the right place either way, and
   * parenting it to the wheel group only matters for something that turns.
   */
  async function paintedCar(car: SavedCar): Promise<SVGSVGElement | null> {
    const [art, pup] = await Promise.all([
      loadArt('vehicles', car.templateId),
      loadArt('pups', pupIdForVehicle(car.templateId)),
    ]);
    if (art === null) return null;

    const frame = viewBoxOf(art);
    await applyDecorations(
      art,
      { colours: car.regionColours, strokes: car.strokes, stickers: car.stickers },
      frame.width,
    );
    if (disposed) return null;

    // `inlineSvg` gives every piece of pack art the `icon` class, which is 74% of the badge; this one
    // is sized as the badge's picture instead.
    art.setAttribute('class', 'hub-car-art');
    if (pup !== null) seatPup(art, pup);
    return art;
  }

  function newCar(ctx: ScreenContext): void {
    dispatch(ctx, { type: 'NEW_CAR' });
  }
}

/**
 * The five slots, by grid area. The class is what pins an action to a position, and it is carried by
 * the empty placeholder as well as by the button - which is what holds the layout still.
 *
 * The order here is the order they pop in, which is the order she reads them in: the crayon first, then
 * the car, then the places.
 */
const SLOT = {
  draw: 'hub-slot--draw',
  drive: 'hub-slot--drive',
  garage: 'hub-slot--garage',
  prizes: 'hub-slot--prizes',
  freeplay: 'hub-slot--freeplay',
} as const;

/** The stagger: each slot's entrance is delayed by its position, read by CSS as `--slot-index`. */
const SLOT_ORDER: readonly string[] = Object.values(SLOT);

function inSlot(node: HTMLElement, slot: string): HTMLElement {
  node.style.setProperty('--slot-index', String(Math.max(0, SLOT_ORDER.indexOf(slot))));
  return node;
}

/**
 * The place itself: the Lookout, and the ground it stands on.
 *
 * The band has a **fixed height** whether the intro is running or finished, and the sequence only moves
 * things inside it. Animating the band's own size would push the five slots down the screen and then let
 * them settle - and the one promise this screen makes is that a button is always in the same place.
 *
 * There is no pup here any more. One stood on the grass beside the tower and it was the wrong place for
 * her twice over: the dog who drives her car belongs *in* her car, which is the badge below, and on a fresh
 * save the only pup this could show was the generic one - a stranger introduced as though she were somebody.
 * See `pupInCab`.
 */
function scene(
  loadArt: (family: string, name: string) => Promise<SVGSVGElement | null>,
): HTMLElement {
  const lookout = el('div', { className: 'hub-lookout' });

  // A missing file costs the picture and nothing else: the slots below are the screen's actual job.
  void loadArt('items', 'lookout').then((svg) => svg !== null && lookout.replaceChildren(svg));

  return el('div', {
    className: 'hub-scene',
    // Decoration, and nothing in it may ever swallow a press meant for a slot.
    attrs: { 'aria-hidden': 'true' },
    children: [ground(), lookout],
  });
}

/**
 * The name, arcing over the tower.
 *
 * A band pinned to the top of the screen rather than a row in the column the slots live in: the slots'
 * promise is that a button is always in the same place, and a title that took part in the layout would move
 * them. The band overlaps the top of the scene, which is empty sky - the arc's shoulders come down at the
 * left and right edges where the tower is not.
 *
 * `brandMark` returning null leaves the band empty, and an empty band is a screen that looks like the one
 * before this pass rather than a broken one.
 *
 * Two marks go in here, one after the other. The drawn one is up in the first frame, which is the whole
 * reason it is drawn rather than fetched; the pack's own letterforms replace it as soon as the sheet
 * arrives, on the same arc and at the same size. A pack without an alphabet never sends a second one, and
 * the screen keeps the first.
 */
function title(alive: () => boolean): HTMLElement {
  const band = el('div', { className: 'hub-title', attrs: { 'aria-hidden': 'true' } });
  const mark = brandMark();
  // Appended rather than passed as a child: `el` takes HTML children, and this one is an SVG.
  if (mark !== null) band.append(mark);

  void packBrandMark().then((packed) => {
    // `alive` and not just `isConnected`: the band is built before the screen is in the document, so a
    // sheet that arrives that fast would find it unconnected and give up for the wrong reason.
    if (packed === null || !alive()) return;
    band.replaceChildren(packed);
  });

  return band;
}

/**
 * The road out, and the ground it crosses.
 *
 * Inside the scene band rather than beside it, and full-bleed from there: the horizon is *by construction*
 * a fixed fraction of the band, which is the only thing that keeps the tower standing on the grass instead
 * of hovering over it as the screen changes shape. Anchoring it to the screen instead would mean two
 * measurements that have to agree, and on a short landscape window they would not.
 *
 * The road leaves the Lookout's door and widens down between the slots, fading out before it reaches them.
 * It is the one piece of the scene that says what the buttons are *for*.
 */
function ground(): HTMLElement {
  const road = el('div', { className: 'hub-road' });
  const art = roadMark();
  if (art !== null) road.append(art);
  return el('div', { className: 'hub-ground', children: [road] });
}

/**
 * The road itself, as an SVG polygon.
 *
 * It was a `clip-path: polygon(...)` on a div, which is the obvious way to do it and is *wrong on the
 * device*: at 1x raster Chromium drops the clip for this layer and paints the gradient into the polygon's
 * bounding box instead, so the road arrives as a tan rectangle 550px wide across the middle of the screen.
 * It renders correctly at 2x, correctly in a partial-region capture, and correctly in every devtools
 * inspection - a screenshot at the size the tablet actually uses is the only thing that shows it.
 *
 * A polygon in an SVG cannot take that path. `preserveAspectRatio="none"` lets the shape stretch to whatever
 * box the CSS gives it, so the geometry still lives in the stylesheet where the rest of the scene is.
 */
function roadMark(): SVGSVGElement | null {
  return parseSvg(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" preserveAspectRatio="none"' +
      ' class="hub-road-art" aria-hidden="true">' +
      '<defs><linearGradient id="hub-road-fade" x1="0" y1="0" x2="0" y2="1">' +
      '<stop offset="0" stop-color="#e6cb95"/>' +
      '<stop offset="0.4" stop-color="#e2c284" stop-opacity="0.9"/>' +
      '<stop offset="1" stop-color="#e2c284" stop-opacity="0"/>' +
      '</linearGradient></defs>' +
      // Out of the door, widening towards her, fading out before it reaches the badges.
      '<polygon points="47.4,0 52.6,0 77,100 23,100" fill="url(#hub-road-fade)"/>' +
      '</svg>',
  );
}

/**
 * SVG authored here rather than fetched, as an element.
 *
 * Null on anything malformed, and every caller on this screen treats that the way it treats a missing file:
 * one fewer decoration, every control intact. A duplicate attribute is enough to fail an XML parse and it
 * fails *silently*, which is worth one shared check - the road and the cab both go through here.
 */
function parseSvg(markup: string): SVGSVGElement | null {
  const parsed = new DOMParser().parseFromString(markup, 'image/svg+xml');
  if (parsed.querySelector('parsererror') !== null) return null;
  return parsed.documentElement as unknown as SVGSVGElement;
}

/**
 * How much of the pane her head fills.
 *
 * The drive scene's number (`PUP_FILLS_PANE` in drive-screen.ts), written out again rather than imported:
 * that module pulls in Pixi, and the first screen of the game should not load a WebGL renderer to draw a dog
 * in a badge. What must not drift is the *framing*, and that is why everything below comes from the pane and
 * her own anchors rather than from taste - the same construction, in SVG.
 */
const PUP_FILLS_PANE = 0.92;

/** Clip paths need a document-unique id, and two Lookouts overlap for the length of a screen fade. */
let cabSeq = 0;

/**
 * Put the pup in the cab of a decorated vehicle SVG: body, then pup, then glass over her.
 *
 * The drive scene's rules, and nothing invented on top of them:
 *
 *  - her **face** lands on `#anchor-seat`, the point the art itself declares as the driver's place. The
 *    windscreen's *bounding box* centre is not that point: on a slanted screen it sits low, which put her
 *    head on the cruiser's door line - and the badge was the only place it showed, because the road has
 *    always used the anchor.
 *  - she is **sized to the pane**, her head filling its shorter side, so a letterbox windscreen does not
 *    blow her up.
 *  - she is **clipped to the pane**, so the parts of her that are not behind glass are not drawn on the
 *    bodywork like a sticker on a door.
 *  - the **glass goes over her**, at the alpha the road uses. `#window` is a real group in this SVG, so
 *    "draw the glass last" is a `append` rather than a second bake: whatever colour she painted the
 *    windscreen reads as tinted glass, and she reads through it.
 *
 * Anything missing - no pane, no anchor, no viewBox on the pup - leaves the car exactly as it was. A badge
 * with her car on it and no dog is the screen this replaced, not a broken one.
 */
function seatPup(art: SVGSVGElement, pup: SVGSVGElement): void {
  const pane = windowBoxOf(art);
  // No viewBox is no coordinate space, and a pup placed in a guessed one would sit somewhere arbitrary.
  const face = pup.getAttribute('viewBox') === null ? null : anchorOf(pup, 'anchor-face');
  if (pane === null || face === null) return;

  // The seat if the art declares one, the middle of the glass if it does not - the drive scene's fallback.
  const seat = anchorOf(art, 'anchor-seat') ?? {
    cx: pane.x + pane.width / 2,
    cy: pane.y + pane.height / 2,
    r: Math.min(pane.width, pane.height) / 2,
  };

  const scale = (Math.min(pane.width, pane.height) * PUP_FILLS_PANE) / (face.r * 2);
  const clipId = `hub-cab-${++cabSeq}`;
  // A throwaway root: XML has to be parsed as a document, and only its two children are kept. Their
  // coordinates are the vehicle's, which is the space they are about to be appended into.
  const parsed = parseSvg(
    `<svg xmlns="http://www.w3.org/2000/svg">` +
      `<defs><clipPath id="${clipId}">` +
      `<rect x="${round(pane.x)}" y="${round(pane.y)}" width="${round(pane.width)}"` +
      ` height="${round(pane.height)}"/>` +
      `</clipPath></defs>` +
      /*
       * The clip and the move are two nested groups, not one element carrying both.
       *
       * `clip-path` resolves in the user space of the element that carries it, so a `transform` on that same
       * element moves the clip as well: the pane went off with the pup and clipped every pixel of her away.
       * A badge with a car on it and no dog, with nothing logged and nothing missing from the DOM - the
       * markup all looked right. The clip stays in template space out here; only the inner group moves.
       */
      `<g class="hub-car-pup" clip-path="url(#${clipId})">` +
      `<g transform="translate(${round(seat.cx - face.cx * scale)} ` +
      `${round(seat.cy - face.cy * scale)}) scale(${round(scale)})"></g>` +
      `</g>` +
      `</svg>`,
  );
  const cab = parsed?.querySelector('.hub-car-pup') ?? null;
  const mover = cab?.querySelector('g') ?? null;
  const defs = parsed?.querySelector('defs') ?? null;
  if (cab === null || mover === null || defs === null) return;

  /*
   * A nested `<svg>` with its own viewport, so the pup keeps her own coordinates and her `data-icon` - and
   * *without* the `icon` class `inlineSvg` gives her, because `.hub-button .icon { width: 74% }` would size
   * her against the badge instead of against the cab.
   */
  const pupFrame = viewBoxOf(pup);
  pup.removeAttribute('class');
  pup.setAttribute('x', '0');
  pup.setAttribute('y', '0');
  pup.setAttribute('width', String(pupFrame.width));
  pup.setAttribute('height', String(pupFrame.height));
  mover.append(pup);

  art.append(defs, cab);

  /*
   * And the windscreen's ink back on top of her, so she is inside the cab rather than stuck on the door.
   *
   * The *frame* only - the pane's own fill stays under her, which is where this differs from the road by a
   * measured amount. The drive scene draws the whole glass over the pup at `GLASS_ALPHA`, and it is right to:
   * at that size the tint reads as glass and her ink still carries through it. On the badge the same pane is
   * some six times smaller, her 7-unit outlines land on about a pixel, and 62% of white over that is a pale
   * smudge in a windscreen - checked side by side against the road before it was changed. So her painted
   * glass shows *around* her and the frame closes over her, which is what says "behind the window" at any
   * size. Moved rather than copied: one set of lines, so the outline stays a single stroke.
   */
  const frame = art.querySelector('#window .lines');
  if (frame !== null) {
    // Tagged on the way out: once it has been moved it is no longer inside `#window`, so this is the only
    // thing that still says what it is - to the stylesheet, to a test, and to whoever inspects the badge.
    frame.setAttribute('class', 'lines hub-car-glass');
    art.append(frame);
  }
}

/** Two decimals: the art is a thousand units across, so this is well under a pixel on any screen. */
function round(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Three clouds, drifting.
 *
 * The sky was a gradient with a soft disc in the corner, which reads as a backdrop; a sky with something
 * moving slowly across it reads as a place. Slow enough (a minute and a half end to end) that nothing on
 * the screen competes with the buttons - the same bargain the discs already made.
 */
function clouds(): HTMLElement {
  return el('div', {
    className: 'hub-clouds',
    attrs: { 'aria-hidden': 'true' },
    children: [0, 1, 2].map((index) =>
      el('div', { className: `hub-cloud hub-cloud--${index + 1}` }),
    ),
  });
}

/**
 * A badge per pup, drifting.
 *
 * Inherited from the boot screen, which is gone: colour is the only branding a pre-reader can read, and
 * these are the same colours as the picker cards. Behind everything, at low opacity, moving slowly enough
 * that the screen is alive without anything competing with the buttons.
 *
 * Each one now carries the paw, which is what a pup's badge actually is - and which the discs could only
 * become once the paw was drawn art rather than an emoji, because a colour emoji ignores `color` and every
 * paw in the game would have been the same shade. All eight share one fetch, and a failure leaves the plain
 * disc this used to be.
 *
 * One per vehicle, so it stays right as the pack grows - the boot screen said "six" in its comment long
 * after Everest and Tracker had made it eight.
 */
function discs(): HTMLElement {
  return el('div', {
    className: 'hub-discs',
    attrs: { 'aria-hidden': 'true' },
    children: VEHICLES.map((vehicle, index) => {
      const disc = el('div', { className: 'hub-disc' });
      disc.style.background = vehicle.badgeColour;
      // Staggered, so they drift one after another rather than breathing in unison.
      disc.style.animationDelay = `${index * 1400}ms`;
      disc.style.setProperty('--disc-index', String(index));
      void menuIconElement('paw').then((paw) => paw !== null && disc.append(paw));
      return disc;
    }),
  });
}

/**
 * A hub button: a picture in a slot, never a label.
 *
 * `pictureButton` rather than `bigButton` because these have no text in them at all - and it owns the
 * `pointerdown` decision, so this screen cannot drift from the others on it.
 */
function hubButton(
  glyph: keyof typeof GLYPH,
  className: string,
  slot: string,
  onPress: () => void,
): HTMLButtonElement {
  return inSlot(
    pictureButton([el('span', { className: 'hub-glyph', text: GLYPH[glyph] })], onPress, {
      className: `hub-button ${slot} ${className}`,
      icon: GLYPH_ICON[glyph],
    }),
    slot,
  ) as HTMLButtonElement;
}

/**
 * A slot held open with nothing in it.
 *
 * A `div`, not a disabled button: there must be nothing here that can be pressed at all. The space is
 * kept so that the buttons around it never move, and *only* the space - a greyed-out button that
 * answers a press with nothing is how she learns the game is broken.
 */
function emptySlot(slot: string): HTMLElement {
  return inSlot(
    el('div', { className: `hub-button is-empty ${slot}`, attrs: { 'aria-hidden': 'true' } }),
    slot,
  );
}

/**
 * Leaving the Lookout.
 *
 * This used to bark. The bark is gone because the button factory now answers every press itself
 * (`onPointerDown` in `dom.ts`), and a tock followed by a dog is two sounds for one press - the second of
 * which said nothing the first had not. The bark is kept where it means a pup *responding*: her very first
 * press of the session, once the audio context is unlocked (`main.ts`), and the unstick button on the road.
 */
function dispatch(ctx: ScreenContext, event: Parameters<ScreenContext['dispatch']>[0]): void {
  ctx.dispatch(event);
}

function mostRecentCar(save: SaveData): SavedCar | null {
  return save.garage.at(-1) ?? null;
}

/**
 * A number for free play's world.
 *
 * `Math.random` is fine *here* - this is the shell, not `src/core`, and nothing about free play needs to be
 * reproducible. Everything downstream is still pure and seeded; only the choice of seed is arbitrary.
 */
function freshSeed(): number {
  return (Math.random() * 0xffffffff) | 0;
}
